import * as THREE from "three";
import {ACTION_TYPE, EDITORMODE, PROPERTY_TAB} from "utils/constants";
import Command from "./command";
import Audio from "./Assets/Audio";
import ScriptLauncher from "utils/UserScript/ScriptLauncher";

const _vector3 = new THREE.Vector3();

export function _updateProperty(context, category, action) {
  if (context.selected === undefined || context.currentPropertyTab === PROPERTY_TAB.SCENE) {
    if (category === "background") {
      switch (action.name) {
        case "color":
          context.sceneEdit.background = new THREE.Color(action.value);
          break;
        case "texture":
          context.sceneEdit.background = context.TextureLoader.load(action.value, texture => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.mapping = THREE.EquirectangularReflectionMapping;
            texture.repeat.set(1, 1);
            context.render();
          });
          break;
        case "default":
          let skyPath = "asset/sky/bluesky/bluesky";
          let sky = {
            SKY: [
              skyPath + "_px.png",
              skyPath + "_nx.png",
              skyPath + "_py.png",
              skyPath + "_ny.png",
              skyPath + "_pz.png",
              skyPath + "_nz.png",
            ],
          };
          context.sceneEdit.background = context.cubeTextureLoader.load(sky.SKY, () => {
            context.sceneEdit.background.name = "blue sky";
            context.sceneEdit.background.key = context.sceneEdit.background.uuid;
            context.sceneEdit.background.title = "blue sky";
            context.sceneEdit.background.userData = {type: "sky"};
            context.sceneEdit.background.encoding = THREE.sRGBEncoding;
            context.sceneEdit.background.needsUpdate = true;
            context.render();
          });
          break;
        default:
          break;
      }
    } else if (category === "environment") {
      switch (action.name) {
        case "input":
          context.sceneEdit.userData.environment = action.value;
          break;
        case "checkbox":
          if (!context.sceneEdit.environment) {
            const texture = context.TextureLoader.load(context.sceneEdit.userData.environment, () => {
              texture.mapping = THREE.EquirectangularReflectionMapping;
              texture.encoding = THREE.sRGBEncoding;
              context.render();
            });
            context.sceneEdit.environment = texture;
          } else {
            context.sceneEdit.environment = null;
          }
          break;
        default:
          break;
      }
    } else if (category === "fog") {
      switch (action.name) {
        case "color":
          context.fogExponential.color = new THREE.Color(action.value);
          context.fogLinear.color = new THREE.Color(action.value);
          break;
        case "active":
          if (action.value) {
            const fogMode = context.sceneEdit.userData.fogMode;
            if (fogMode === "Exponential") {
              context.sceneEdit.fog = context.fogExponential;
            } else if (fogMode === "Linear") {
              context.sceneEdit.fog = context.fogLinear;
            } else {
              // default fog mode
              context.sceneEdit.fog = context.fogExponential;
            }
          } else {
            context.sceneEdit.fog = null;
          }
          break;
        case "density":
          context.fogExponential.density = action.value;
          break;
        case "near":
          context.fogLinear.near = action.value;
          break;
        case "far":
          context.fogLinear.far = action.value;
          break;
        case "mode":
          context.sceneEdit.userData.fogMode = action.value;
          if (action.value === "Exponential") {
            context.sceneEdit.fog = context.fogExponential;
          } else if (action.value === "Linear") {
            context.sceneEdit.fog = context.fogLinear;
          }
          break;
        default:
          break;
      }
    } else if (category === "tone") {
      switch (action.name) {
        case "mode":
          if (action.value === "None") {
            context.sceneEdit.userData.toneMapping.mode = THREE.NoToneMapping;
            context.renderer.glRenderer.toneMapping = THREE.NoToneMapping;
          } else if (action.value === "Linear") {
            context.sceneEdit.userData.toneMapping.mode = THREE.LinearToneMapping;
            context.renderer.glRenderer.toneMapping = THREE.LinearToneMapping;
          } else if (action.value === "Reinhard") {
            context.sceneEdit.userData.toneMapping.mode = THREE.ReinhardToneMapping;
            context.renderer.glRenderer.toneMapping = THREE.ReinhardToneMapping;
          } else if (action.value === "Cineon") {
            context.sceneEdit.userData.toneMapping.mode = THREE.CineonToneMapping;
            context.renderer.glRenderer.toneMapping = THREE.CineonToneMapping;
          } else if (action.value === "ACES Filmic") {
            context.sceneEdit.userData.toneMapping.mode = THREE.ACESFilmicToneMapping;
            context.renderer.glRenderer.toneMapping = THREE.ACESFilmicToneMapping;
          }
          break;
        case "exposure":
          context.sceneEdit.userData.toneMapping.exposure = action.value;
          context.renderer.glRenderer.toneMappingExposure = action.value;
          break;
        default:
          break;
      }
    } else if (category === "ssao") {
      switch (action.name) {
        case "active":
          if (action.value) {
            context.renderer.createSSAO(context.sceneEdit, context.editCamera);
          } else {
            context.renderer.removeSSAO(true);
          }
          break;
        case "bias":
        case "intensity":
        case "fade":
        case "color":
          context.renderer.updateSSAO({
            [action.name]: action.value,
          });
          break;
        default:
          break;
      }
    } else if (category === "bloom") {
      switch (action.name) {
        case "active":
          if (action.value) {
            context.renderer.createBloom(context.sceneEdit, context.editCamera);
          } else {
            context.renderer.removeBloom(true);
          }
          break;
        case "luminance":
        case "inverted":
        case "intensity":
        case "radius":
        case "opacity":
        case "blendFunction":
        case "threshold":
        case "smoothing":
          context.renderer.updateBloom({
            [action.name]: action.value,
          });
          break;
        default:
          break;
      }
    } else if (category === "hueSaturation") {
      switch (action.name) {
        case "active":
          if (action.value) {
            context.renderer.createHueSaturation(context.sceneEdit, context.editCamera);
          } else {
            context.renderer.removeHueSaturation(true);
          }
          break;
        case "blendFunction":
          context.renderer.updateHueSaturation({
            [action.name]: action.value,
          });
          break;
        case "hue":
          context.renderer.updateHueSaturation({
            [action.name]: action.value,
          });
          break;
        case "saturation":
          context.renderer.updateHueSaturation({
            [action.name]: action.value,
          });
          break;
        case "opacity":
          context.renderer.updateHueSaturation({
            [action.name]: action.value,
          });
          break;
        default:
          break;
      }
    } else if (category === "brightnessContrast") {
      switch (action.name) {
        case "active":
          if (action.value) {
            context.renderer.createBrightnessContrast(context.sceneEdit, context.editCamera);
          } else {
            context.renderer.removeBrightnessContrast(true);
          }
          break;
        case "blendFunction":
        case "brightness":
        case "contrast":
        case "opacity":
          context.renderer.updateBrightnessContrast({
            [action.name]: action.value,
          });
          break;
        default:
          break;
      }
    } else if (category === "godrays") {
      switch (action.name) {
        case "active":
          if (action.value) {
            context.renderer.createGodrays(context.sceneEdit, context.editCamera);
          } else {
            context.renderer.removeGodrays(true);
          }
          break;
        case "color":
        case "weight":
        case "density":
          context.renderer.updateGodrays({
            [action.name]: action.value,
          });
          break;
        default:
          break;
      }
    } else if (category === "script") {
      const {name, value} = action;

      switch (name) {
        case "active":
          context.sceneEdit.userData.canScript = value;
          break;
        case "serverScriptTitle":
          context.sceneEdit.userData.objectScripts.serverScripts[value.index].title = value.title;
          break;
        case "clientScriptTitle":
          context.sceneEdit.userData.objectScripts.clientScripts[value.index].title = value.title;
          break;
        case "addServerScript":
          if (!context.sceneEdit.userData.objectScripts) {
            context.sceneEdit.userData.objectScripts = {
              serverScripts: [],
              clientScripts: [],
            };
          }
          context.sceneEdit.userData.objectScripts.serverScripts.push({title: "", source: ScriptLauncher.defaultScript});
          context.sceneEdit.userData.canScript = true;
          break;
        case "addClientScript":
          if (!context.sceneEdit.userData.objectScripts) {
            context.sceneEdit.userData.objectScripts = {
              serverScripts: [],
              clientScripts: [],
            };
          }
          context.sceneEdit.userData.objectScripts.clientScripts.push({title: "", source: ScriptLauncher.defaultScript});
          context.sceneEdit.userData.canScript = true;
          break;
        case "editServerScript":
          if (context.selectedScript) context.selectedScript.source = value.source;
          else context.sceneEdit.userData.objectScripts.serverScripts[value.index].source = value.source;
          break;
        case "editClientScript":
          if (context.selectedScript) context.selectedScript.source = value.source;
          else context.sceneEdit.userData.objectScripts.clientScripts[value.index].source = value.source;
          break;
        case "removeServerScript":
          context.sceneEdit.userData.objectScripts.serverScripts.splice(value, 1);
          context.sceneEdit.userData.canScript =
            context.sceneEdit.userData.objectScripts.serverScripts.length >= 1 ||
            context.sceneEdit.userData.objectScripts.clientScripts.length >= 1;
          break;
        case "removeClientScript":
          context.sceneEdit.userData.objectScripts.clientScripts.splice(value, 1);
          context.sceneEdit.userData.canScript =
            context.sceneEdit.userData.objectScripts.serverScripts.length >= 1 ||
            context.sceneEdit.userData.objectScripts.clientScripts.length >= 1;
          break;
        default:
          break;
      }
    }
    //render
    context.dispatchEvent(
      new CustomEvent(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, {
        detail: {object: context.selected},
      }),
    );
    context.render();
    return; // below need to be selected
  }

  let commandObj = {target: context.selected};
  let timeDifference = new Date().getTime() - context.lastCmdTime.getTime();

  const handleCommand = (type, preValue, value) => {
    commandObj.type = type;
    commandObj.preValue = preValue;
    commandObj.postValue = value || action.value;
    let command = new Command(commandObj);
    context.undo.push(command);
  };

  if (category === "name") {
    context.selected.name = context.selected.title = action.value;
  } else if (category === "title") {
    if (context.codeEditorMode === EDITORMODE.JAVASCRIPT_3D) {
      // editing title in OOBC is not recommended yet due to OOBC's UI
      context.selected.title = context.selected.userData.title = action.value;
    }
  } else if (category === "body") {
    context.selected.userData.hasBody = action.value;
  } else if (category === "mass") {
    context.selected.userData.mass = action.value;
  } else if (category === "friction") {
    if (action.value >= 1) {
      context.selected.userData.friction = 1;
    } else if (action.value <= 0) {
      context.selected.userData.friction = 0;
    } else {
      context.selected.userData.friction = action.value;
    }
  } else if (category === "bodyType") {
    switch (action.value) {
      case "Default":
        context.selected.userData.bodyType = undefined;
        break;
      case "Box":
        context.selected.userData.bodyType = "box";
        const box = new THREE.Box3().setFromObject(context.selected);

        context.selected.userData.width = box.max.x - box.min.x;
        context.selected.userData.height = box.max.y - box.min.y;
        context.selected.userData.depth = box.max.z - box.min.z;
        break;
      case "Sphere":
        context.selected.userData.bodyType = "sphere";
        const boundingBox = new THREE.Box3().setFromObject(context.selected);

        const radius =
          (boundingBox.max.x - boundingBox.min.x + (boundingBox.max.y - boundingBox.min.y) + (boundingBox.max.z - boundingBox.min.z)) / 6;

        context.selected.userData.radius = radius;
        break;
      case "Hull":
        context.selected.userData.bodyType = "hull";
        break;
      case "Convex":
        context.selected.userData.bodyType = "convex";
        break;
      case "Concave":
        context.selected.userData.bodyType = "concave";
        break;
      default:
        break;
    }
  } else if (category === "position") {
    commandObj.type = Command.typePosition;
    commandObj.preValue = context.selected.position.clone();
    commandObj.preUserData = {
      x: context.selected.userData.positionX,
      y: context.selected.userData.positionY,
      z: context.selected.userData.positionZ,
    };
    switch (action.name) {
      case "x":
        if (Number.isFinite(Number(action.value)) && action.value !== "") context.selected.position.setX(Number(action.value));
        context.selected.userData.temporalInput.position.x = action.value;
        break;
      case "y":
        if (Number.isFinite(Number(action.value)) && action.value !== "") context.selected.position.setY(Number(action.value));
        context.selected.userData.temporalInput.position.y = action.value;
        break;
      case "z":
        if (Number.isFinite(Number(action.value)) && action.value !== "") context.selected.position.setZ(Number(action.value));
        context.selected.userData.temporalInput.position.z = action.value;
        break;
      default:
        break;
    }
    commandObj.postValue = context.selected.position.clone();
    commandObj.postUserData = {
      x: context.selected.userData.positionX,
      y: context.selected.userData.positionY,
      z: context.selected.userData.positionZ,
    };
    let command = new Command(commandObj);
    context.undo.push(command);
  } else if (category === "rotation") {
    commandObj.type = Command.typeRotation;
    commandObj.preValue = context.selected.rotation.clone();
    commandObj.preUserData = {
      x: context.selected.userData.rotationX,
      y: context.selected.userData.rotationY,
      z: context.selected.userData.rotationZ,
    };
    switch (action.name) {
      case "x":
        if (Number.isFinite(Number(action.value)) && action.value !== "")
          context.selected.rotation.x = action.value * THREE.MathUtils.DEG2RAD;
        context.selected.userData.temporalInput.rotation.x = action.value;
        break;
      case "y":
        if (Number.isFinite(Number(action.value)) && action.value !== "")
          context.selected.rotation.y = action.value * THREE.MathUtils.DEG2RAD;
        context.selected.userData.temporalInput.rotation.y = action.value;
        break;
      case "z":
        if (Number.isFinite(Number(action.value)) && action.value !== "")
          context.selected.rotation.z = action.value * THREE.MathUtils.DEG2RAD;
        context.selected.userData.temporalInput.rotation.z = action.value;
        break;
      default:
        break;
    }
    commandObj.postValue = context.selected.rotation.clone();
    commandObj.postUserData = {
      x: context.selected.userData.rotationX,
      y: context.selected.userData.rotationY,
      z: context.selected.userData.rotationZ,
    };
    let command = new Command(commandObj);
    context.undo.push(command);
  } else if (category === "scale") {
    commandObj.type = Command.typeScale;
    commandObj.preValue = context.selected.scale.clone();
    commandObj.preUserData = {
      x: context.selected.userData.scaleX,
      y: context.selected.userData.scaleY,
      z: context.selected.userData.scaleZ,
    };
    switch (action.name) {
      case "x":
        if (Number.isFinite(Number(action.value)) && action.value !== "") context.selected.scale.setX(Number(action.value));
        context.selected.userData.temporalInput.scale.x = action.value;
        break;
      case "y":
        if (Number.isFinite(Number(action.value)) && action.value !== "") context.selected.scale.setY(Number(action.value));
        context.selected.userData.temporalInput.scale.y = action.value;
        break;
      case "z":
        if (Number.isFinite(Number(action.value)) && action.value !== "") context.selected.scale.setZ(Number(action.value));
        context.selected.userData.temporalInput.scale.z = action.value;
        break;
      default:
        break;
    }
    commandObj.postValue = context.selected.scale.clone();
    commandObj.postUserData = {
      x: context.selected.userData.scaleX,
      y: context.selected.userData.scaleY,
      z: context.selected.userData.scaleZ,
    };
    let command = new Command(commandObj);
    context.undo.push(command);
  } else if (category === "material") {
    const material = context.selected.material;
    switch (action.name) {
      case "color":
        commandObj.type = Command.typeMaterialColor;
        if (timeDifference < 500) {
          let lastCmd = context.undo[context.undo.length - 1];
          if (lastCmd.type === Command.typeMaterialColor) lastCmd.postValue = "0x" + action.value.slice(1);
        } else {
          commandObj.preValue = material.color.getHex();
          commandObj.postValue = "0x" + action.value.slice(1);
          let command = new Command(commandObj);
          context.undo.push(command);
        }
        material.color.setHex("0x" + action.value.slice(1));
        break;
      case "shininess":
        handleCommand(Command.typeShininess, material.shininess);
        material.shininess = action.value;
        break;
      case "side":
        switch (action.value) {
          case "Back":
            material.side = THREE.BackSide;
            break;
          case "Front":
            material.side = THREE.FrontSide;
            break;
          case "Double":
            material.side = THREE.DoubleSide;
            break;
          default:
            break;
        }
        break;
      case "map-input":
        handleCommand(Command.typeMap, material.map);
        if (action.value === null) {
          material.map = null;
          material.needsUpdate = true;
        } else if (context.selected.userData.hasMap) {
          material.transparent = true;
          const texture = context.TextureLoader.load(context.selected.userData.map, () => {
            material.map = texture;
            material.map.wrapS = material.map.wrapT = THREE.RepeatWrapping;
            context.render();
            material.transparent = material.opacity !== 1;
            material.needsUpdate = true;
          });
          material.needsUpdate = true;
        }

        break;
      case "map-checkbox":
        handleCommand(Command.typeMap, material.map);
        if (action.value) {
          material.transparent = true;
          const texture = context.TextureLoader.load(context.selected.userData.map, () => {
            material.map = texture;
            material.map.wrapS = material.map.wrapT = THREE.RepeatWrapping;
            context.render();
            material.transparent = material.opacity !== 1;
          });
          context.selected.userData.hasMap = true;
        } else {
          material.map = null;
          material.needsUpdate = true;
          context.selected.userData.hasMap = false;
        }
        break;
      case "normalMap-input":
        handleCommand(Command.typeMap, material.normalMap);
        if (action.value === null) {
          material.normalMap = null;
          material.needsUpdate = true;
        } else if (context.selected.userData.hasNormalMap) {
          material.transparent = true;
          const texture = context.TextureLoader.load(context.selected.userData.normalMap, () => {
            material.normalMap = texture;
            material.normalMap.wrapS = material.normalMap.wrapT = THREE.RepeatWrapping;
            context.render();
            material.transparent = material.opacity !== 1;
            material.needsUpdate = true;
          });
          material.needsUpdate = true;
        }
        break;
      case "normalMap-checkbox":
        handleCommand(Command.typeMap, material.normalMap);
        if (action.value) {
          material.transparent = true;
          const texture = context.TextureLoader.load(context.selected.userData.normalMap, () => {
            material.normalMap = texture;
            material.normalMap.wrapS = material.normalMap.wrapT = THREE.RepeatWrapping;
            context.render();
            material.transparent = material.opacity !== 1;
          });
          context.selected.userData.hasNormalMap = true;
        } else {
          material.normalMap = null;
          material.needsUpdate = true;
          context.selected.userData.hasNormalMap = false;
        }
        break;
      case "envMap-input":
        handleCommand(Command.typeMap, material.envMap);
        if (action.value === null) {
          material.envMap = null;
          material.needsUpdate = true;
        } else if (context.selected.userData.hasEnvMap) {
          material.transparent = true;
          const texture = context.TextureLoader.load(context.selected.userData.envMap, () => {
            material.envMap = texture;
            material.envMap.wrapS = material.envMap.wrapT = THREE.RepeatWrapping;
            texture.mapping = THREE.EquirectangularReflectionMapping;
            context.render();
            material.transparent = material.opacity !== 1;
            material.needsUpdate = true;
          });
          material.needsUpdate = true;
        }
        break;
      case "envMap-checkbox":
        handleCommand(Command.typeMap, material.envMap);
        if (action.value) {
          material.transparent = true;
          const texture = context.TextureLoader.load(context.selected.userData.envMap, () => {
            material.envMap = texture;
            material.envMap.wrapS = material.envMap.wrapT = THREE.RepeatWrapping;
            texture.mapping = THREE.EquirectangularReflectionMapping;
            context.render();
            material.transparent = material.opacity !== 1;
          });
          context.selected.userData.hasEnvMap = true;
        } else {
          material.envMap = null;
          material.needsUpdate = true;
          context.selected.userData.hasEnvMap = false;
        }
        break;
      case "blending":
        switch (action.value) {
          case "Normal":
            handleCommand(Command.typeBlending, material.blending, THREE.NormalBlending);

            material.blending = THREE.NormalBlending;
            break;
          case "Additive":
            handleCommand(Command.typeBlending, material.blending, THREE.AdditiveBlending);
            material.blending = THREE.AdditiveBlending;
            break;
          case "Subtractive":
            handleCommand(Command.typeBlending, material.blending, THREE.SubtractiveBlending);
            material.blending = THREE.SubtractiveBlending;
            break;
          case "Multiply":
            handleCommand(Command.typeBlending, material.blending, THREE.MultiplyBlending);
            material.blending = THREE.MultiplyBlending;
            break;
          default:
            break;
        }
        break;
      case "opacity":
        if (!material.transparent) material.transparent = true;
        handleCommand(Command.typeOpacity, material.transparent);
        material.opacity = action.value;
        break;
      case "emissiveIntensity":
        handleCommand(Command.typeBlending, material.emissiveIntensity);
        material.emissiveIntensity = action.value;
        break;
      case "emissive":
        handleCommand(Command.typeBlending, material.emissive, "0x" + action.value.slice(1));
        material.emissive.setHex("0x" + action.value.slice(1));
        break;
      case "offsetX":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        handleCommand(Command.typeMaterialOffsetX, material.map?.offset.x);
        if (material.map) material.map.offset.x = action.value;
        if (material.normalMap) material.normalMap.offset.x = action.value;
        break;
      case "offsetY":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        handleCommand(Command.typeMaterialOffsetY, material.map?.offset.y);
        if (material.map) material.map.offset.y = action.value;
        if (material.normalMap) material.normalMap.offset.x = action.value;
        break;
      case "repeatX":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        handleCommand(Command.typeMaterialRepeatX, material.map?.repeat.x);
        if (material.map) material.map.repeat.x = action.value;
        if (material.normalMap) material.normalMap.repeat.x = action.value;
        break;
      case "repeatY":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        handleCommand(Command.typeMaterialRepeatY, material.map?.repeat.y);
        if (material.map) material.map.repeat.y = action.value;
        if (material.normalMap) material.normalMap.repeat.y = action.value;
        break;
      case "rotation":
        handleCommand(Command.typeMaterialRotation, material.map?.rotation);
        if (material.map) material.map.rotation = action.value;
        if (material.normalMap) material.normalMap.rotation = action.value;
        break;
      default:
        break;
    }
  } else if (category === "shadow") {
    context.selected.castShadow = action.value;
    // context.selected.receiveShadow = action.value;
  } else if (category === "cancollide") {
    context.selected.userData.canCollide = action.value;
  } else if (category === "static") {
    context.selected.userData.static = action.value;
  } else if (category === "anchored") {
    context.selected.userData.isKinematic = action.value;
  } else if (category === "massless") {
    context.selected.userData.isMassless = action.value;
  } else if (category === "transparency") {
    const transparent = context.selected.isSprite ? true : action.value !== 1;
    context.selected.traverse(child => {
      if (child.material) {
        child.material.transparent = transparent;
        child.material.opacity = action.value;
      }
    });
    if (context.selected.userData.type === "asset") {
      context.selected.userData.opacity = action.value;
    }
  } else if (category === "light") {
    switch (action.name) {
      case "color":
        commandObj.type = Command.typeLightColor;
        if (timeDifference < 500) {
          let lastCmd = context.undo[context.undo.length - 1];
          if (lastCmd.type === Command.typeLightColor) lastCmd.postValue = "0x" + action.value.slice(1);
        } else {
          commandObj.preValue = context.selected.color.getHex();
          commandObj.postValue = "0x" + action.value.slice(1);
          let command = new Command(commandObj);
          context.undo.push(command);
        }
        context.selected.color.setHex("0x" + action.value.slice(1));
        break;
      case "intensity":
        commandObj.type = Command.typeLightIntensity;
        if (timeDifference < 500) {
          let lastCmd = context.undo[context.undo.length - 1];
          if (lastCmd.type === Command.typeLightIntensity) lastCmd.postValue = action.value;
        } else {
          commandObj.preValue = context.selected.intensity;
          commandObj.postValue = action.value;
          let command = new Command(commandObj);
          context.undo.push(command);
        }
        context.selected.intensity = action.value;
        break;
      case "distance":
        handleCommand(Command.typeLightDistance, context.selected.distance);
        context.selected.distance = action.value;
        break;
      case "target-x":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        handleCommand(Command.typeSpotLightTargetX, context.selected.target.position.x);
        context.selected.target.position.x = action.value;
        context.sceneEdit.add(context.selected.target);
        break;
      case "target-y":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        handleCommand(Command.typeSpotLightTargetY, context.selected.target.y);
        context.selected.target.position.y = action.value;
        context.sceneEdit.add(context.selected.target);
        break;
      case "target-z":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.target.position.z = action.value;
        context.sceneEdit.add(context.selected.target);
        break;
      default:
        break;
    }
  } else if (category === "hemisphereLight") {
    switch (action.name) {
      case "color":
        handleCommand(Command.typeHemisphereLightColor, context.selected.color);
        context.selected.color = action.value;
        break;
      case "groundColor":
        handleCommand(Command.typeHemisphereLightGroundColor, context.selected.groundColor);
        context.selected.groundColor = new THREE.Color(action.value);
        break;
      default:
        break;
    }
  } else if (category === "video") {
    switch (action.name) {
      case "source":
        context.selected.userData.src = action.value;
        break;
      case "doubleside":
        if (action.value) context.selected.material.side = THREE.DoubleSide;
        else context.selected.material.side = THREE.FrontSide;
        context.selected.userData.isDoubleSide = action.value;
        break;
      case "mute":
        context.selected.userData.isVideoMute = action.value;
        break;
      case "autoplay":
        context.selected.userData.isVideoAutoPlay = action.value;
        break;
      default:
        break;
    }
  } else if (category === "script") {
    const {name, value} = action;

    switch (name) {
      case "active":
        context.selected.userData.canScript = value;
        break;
      case "serverScriptTitle":
        context.selected.userData.objectScripts.serverScripts[value.index].title = value.title;
        break;
      case "clientScriptTitle":
        context.selected.userData.objectScripts.clientScripts[value.index].title = value.title;
        break;
      case "addServerScript":
        if (!context.selected.userData.objectScripts) {
          context.selected.userData.objectScripts = {
            serverScripts: [],
            clientScripts: [],
          };
        }
        context.selected.userData.objectScripts.serverScripts.push({title: "", source: ScriptLauncher.defaultScript});
        context.selected.userData.canScript = true;
        break;
      case "addClientScript":
        if (!context.selected.userData.objectScripts) {
          context.selected.userData.objectScripts = {
            serverScripts: [],
            clientScripts: [],
          };
        }
        context.selected.userData.objectScripts.clientScripts.push({title: "", source: ScriptLauncher.defaultScript});
        context.selected.userData.canScript = true;
        break;
      case "editServerScript":
        if (context.selectedScript) context.selectedScript.source = value.source;
        else context.selected.userData.objectScripts.serverScripts[value.index].source = value.source;
        break;
      case "editClientScript":
        if (context.selectedScript) context.selectedScript.source = value.source;
        else context.selected.userData.objectScripts.clientScripts[value.index].source = value.source;
        break;
      case "removeServerScript":
        context.selected.userData.objectScripts.serverScripts.splice(value, 1);
        context.selected.userData.canScript =
          context.selected.userData.objectScripts.serverScripts.length >= 1 ||
          context.selected.userData.objectScripts.clientScripts.length >= 1;
        break;
      case "removeClientScript":
        context.selected.userData.objectScripts.clientScripts.splice(value, 1);
        context.selected.userData.canScript =
          context.selected.userData.objectScripts.serverScripts.length >= 1 ||
          context.selected.userData.objectScripts.clientScripts.length >= 1;
        break;
      default:
        break;
    }
  } else if (category === "text") {
    switch (action.name) {
      // case "active":
      //   context.selected.userData.hasText = action.value;
      //   break;
      case "source":
        context.selected.children.find(child => child.isCSS2DObject).element.textContent = action.value;
        context.selected.userData.css.textContent = action.value;
        break;
      case "color":
        context.selected.children.find(child => child.isCSS2DObject).element.style.color = action.value;
        context.selected.userData.css.fontColor = action.value;
        break;
      case "size":
        context.selected.userData.css.fontSize = action.value + "px";
        context.updateSpriteTransform(context.selected);
        break;
      case "weight":
        context.selected.children.find(child => child.isCSS2DObject).element.style.fontWeight = action.value;
        context.selected.userData.css.fontWeight = action.value;
        break;
      case "font":
        if (action.value === "default") {
          context.selected.children.find(child => child.isCSS2DObject).element.style.fontFamily = "";
          context.selected.userData.css.fontFamily = "";
          break;
        }
        context.selected.children.find(child => child.isCSS2DObject).element.style.fontFamily = action.value;
        context.selected.userData.css.fontFamily = action.value;
        break;
      default:
        break;
    }
  } else if (category === "audio") {
    switch (action.name) {
      case "active":
        if (action.value === true) {
          context.selected.userData.audioInfo = Audio.createAudioInObject();
          context.selected.userData.hasAudio = true;
        } else {
          delete context.selected.userData.audioInfo;
          context.selected.userData.hasAudio = false;
        }
        break;
      case "source":
        context.selected.userData.audioInfo.name = action.value.name;
        context.selected.userData.audioInfo.path = action.value.path;
        context.selected.userData.audioInfo.duration.default = action.value.duration;
        context.selected.userData.audioInfo.loop.loopEnd.default = action.value.duration;
        break;
      case "volume":
        context.selected.userData.audioInfo.volume = action.value;
        break;
      case "playbackRate":
        context.selected.userData.audioInfo.playbackRate = action.value;
        break;
      case "offsetActive":
        context.selected.userData.audioInfo.offset.isActive = action.value;
        break;
      case "offset":
        context.selected.userData.audioInfo.offset.value = action.value;
        break;
      case "durationActive":
        context.selected.userData.audioInfo.duration.isActive = action.value;
        break;
      case "duration":
        context.selected.userData.audioInfo.duration.value = action.value;
        break;
      case "autoplay":
        context.selected.userData.audioInfo.autoplay = action.value;
        break;
      case "positional":
        context.selected.userData.audioInfo.positional.isActive = action.value;
        break;
      case "distanceModel":
        context.selected.userData.audioInfo.positional.distanceModel = action.value;
        break;
      case "refDistance":
        context.selected.userData.audioInfo.positional.refDistance = action.value;
        break;
      case "maxDistance":
        context.selected.userData.audioInfo.positional.maxDistance = action.value;
        break;
      case "rolloffFactor":
        context.selected.userData.audioInfo.positional.rolloffFactor = action.value;
        break;
      case "directional":
        context.selected.userData.audioInfo.positional.directionalCone.isActive = action.value;
        break;
      case "innerAngle":
        context.selected.userData.audioInfo.positional.directionalCone.coneInnerAngle = action.value;
        break;
      case "outerAngle":
        context.selected.userData.audioInfo.positional.directionalCone.coneOuterAngle = action.value;
        break;
      case "outerGain":
        context.selected.userData.audioInfo.positional.directionalCone.coneOuterGain = action.value;
        break;
      case "loop":
        context.selected.userData.audioInfo.loop.isActive = action.value;
        break;
      case "loopStartActive":
        context.selected.userData.audioInfo.loop.loopStart.isActive = action.value;
        break;
      case "loopStart":
        context.selected.userData.audioInfo.loop.loopStart.value = action.value;
        break;
      case "loopEndActive":
        context.selected.userData.audioInfo.loop.loopEnd.isActive = action.value;
        break;
      case "loopEnd":
        context.selected.userData.audioInfo.loop.loopEnd.value = action.value;
        break;
      default:
        break;
    }
  } else if (category === "portal") {
    switch (action.name) {
      case "pid":
        context.selected.userData.pId = action.value;
        break;
      default:
        break;
    }
  } else if (category === "particleSystem") {
    switch (action.name) {
      case "emitAmount-min":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].rate.particlesMin = action.value;
        context.selected.userData.particlesMin = action.value;
        context.selected.resetEmitter(0);
        break;
      case "emitAmount-max":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].rate.particlesMax = action.value;
        context.selected.userData.particlesMax = action.value;
        context.selected.resetEmitter(0);
        break;
      case "emitRate-min":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].rate.perSecondMin = action.value;
        context.selected.userData.perSecondMin = action.value;
        context.selected.resetEmitter(0);
        break;
      case "emitRate-max":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].rate.perSecondMax = action.value;
        context.selected.userData.perSecondMax = action.value;
        context.selected.resetEmitter(0);
        break;
      case "particleLife-min":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "Life";
        }).properties.min = action.value;
        context.selected.userData.lifeMin = action.value;
        context.selected.resetEmitter(0);
        break;
      case "particleLife-max":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "Life";
        }).properties.max = action.value;
        context.selected.userData.lifeMax = action.value;
        context.selected.resetEmitter(0);
        break;
      case "particleMass-min":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "Mass";
        }).properties.min = action.value;
        context.selected.userData.massMin = action.value;
        context.selected.resetEmitter(0);
        break;
      case "particleMass-max":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "Mass";
        }).properties.max = action.value;
        context.selected.userData.massMax = action.value;
        context.selected.resetEmitter(0);
        break;
      case "radialSpeed":
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "RadialVelocity";
        }).properties.radius = action.value;
        context.selected.userData.radialSpeed = action.value;
        context.selected.resetEmitter(0);
        break;
      case "radialDegree":
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "RadialVelocity";
        }).properties.theta = action.value;
        context.selected.userData.radialTheta = action.value;
        context.selected.resetEmitter(0);
        break;
      case "radialDirection-x":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "RadialVelocity";
        }).properties.x = action.value;
        context.selected.userData.radialX = action.value;
        context.selected.resetEmitter(0);
        break;
      case "radialDirection-y":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "RadialVelocity";
        }).properties.y = action.value;
        context.selected.userData.radialY = action.value;
        context.selected.resetEmitter(0);
        break;
      case "radialDirection-z":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "RadialVelocity";
        }).properties.z = action.value;
        context.selected.userData.radialZ = action.value;
        context.selected.resetEmitter(0);
        break;
      case "alphaStart":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Alpha";
        }).properties.alphaA = action.value;
        context.selected.userData.alphaA = action.value;
        context.selected.resetEmitter(0);
        break;
      case "alphaEnd":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Alpha";
        }).properties.alphaB = action.value;
        context.selected.userData.alphaB = action.value;
        context.selected.resetEmitter(0);
        break;
      case "scaleStart":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Scale";
        }).properties.scaleA = action.value;
        context.selected.userData.scaleA = action.value;
        context.selected.resetEmitter(0);
        break;
      case "scaleEnd":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Scale";
        }).properties.scaleB = action.value;
        context.selected.userData.scaleB = action.value;
        context.selected.resetEmitter(0);
        break;
      case "colorStart":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Color";
        }).properties.colorA = action.value;
        context.selected.userData.colorA = action.value;
        context.selected.resetEmitter(0);
        break;
      case "colorEnd":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Color";
        }).properties.colorB = action.value;
        context.selected.userData.colorB = action.value;
        context.selected.resetEmitter(0);
        break;
      case "attraction-x":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Attraction";
        }).properties.x = action.value;
        context.selected.userData.attractionX = action.value;
        context.selected.resetEmitter(0);
        break;
      case "attraction-y":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Attraction";
        }).properties.y = action.value;
        context.selected.userData.attractionY = action.value;
        context.selected.resetEmitter(0);
        break;
      case "attraction-z":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Attraction";
        }).properties.z = action.value;
        context.selected.userData.attractionZ = action.value;
        context.selected.resetEmitter(0);
        break;
      case "attractionForce":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Attraction";
        }).properties.force = action.value;
        context.selected.userData.attractionForce = action.value;
        context.selected.resetEmitter(0);
        break;
      case "force-x":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Force";
        }).properties.fx = action.value;
        context.selected.userData.forceX = action.value;
        context.selected.resetEmitter(0);
        break;
      case "force-y":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Force";
        }).properties.fy = action.value;
        context.selected.userData.forceY = action.value;
        context.selected.resetEmitter(0);
        break;
      case "force-z":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Force";
        }).properties.fz = action.value;
        context.selected.userData.forceZ = action.value;
        context.selected.resetEmitter(0);
        break;
      case "randomDrift-x":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "RandomDrift";
        }).properties.x = action.value;
        context.selected.userData.randomDriftX = action.value;
        context.selected.resetEmitter(0);
        break;
      case "randomDrift-y":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "RandomDrift";
        }).properties.y = action.value;
        context.selected.userData.randomDriftY = action.value;
        context.selected.resetEmitter(0);
        break;
      case "randomDrift-z":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "RandomDrift";
        }).properties.z = action.value;
        context.selected.userData.randomDriftZ = action.value;
        context.selected.resetEmitter(0);
        break;
      case "springDirection-x":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Spring";
        }).properties.x = action.value;
        context.selected.userData.springX = action.value;
        context.selected.resetEmitter(0);
        break;
      case "springDirection-y":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Spring";
        }).properties.y = action.value;
        context.selected.userData.springY = action.value;
        context.selected.resetEmitter(0);
        break;
      case "springDirection-z":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Spring";
        }).properties.z = action.value;
        context.selected.userData.springZ = action.value;
        context.selected.resetEmitter(0);
        break;
      case "springFactor":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Spring";
        }).properties.spring = action.value;
        context.selected.userData.springFactor = action.value;
        context.selected.resetEmitter(0);
        break;
      case "springFriction":
        context.selected.userData.system.emitters[0].behaviours.find(behavior => {
          return behavior.type === "Spring";
        }).properties.friction = action.value;
        context.selected.userData.springFriction = action.value;
        context.selected.resetEmitter(0);
        break;
      case "particleTexture-input":
        context.selected.userData.system.emitters[0].initializers.find(initializer => {
          return initializer.type === "BodySprite";
        }).properties.texture = action.value;
        context.selected.userData.image = action.value;
        context.selected.resetEmitter(0);
        break;
      case "attraction-checkbox":
        if (action.value) {
          context.selected.userData.system.emitters[0].behaviours.push({
            type: "Attraction",
            properties: {
              x: context.selected.userData.attractionX,
              y: context.selected.userData.attractionY,
              z: context.selected.userData.attractionZ,
              force: context.selected.userData.attractionForce,
            },
          });
        } else {
          context.selected.userData.system.emitters[0].behaviours = context.selected.userData.system.emitters[0].behaviours.filter(
            behavior => {
              return behavior.type !== "Attraction";
            },
          );
        }
        context.selected.userData.isAttractionActive = action.value;
        context.selected.resetEmitter(0);
        break;
      case "force-checkbox":
        if (action.value) {
          context.selected.userData.system.emitters[0].behaviours.push({
            type: "Force",
            properties: {
              fx: context.selected.userData.forceX,
              fy: context.selected.userData.forceY,
              fz: context.selected.userData.forceZ,
            },
          });
        } else {
          context.selected.userData.system.emitters[0].behaviours = context.selected.userData.system.emitters[0].behaviours.filter(
            behavior => {
              return behavior.type !== "Force";
            },
          );
        }
        context.selected.userData.isForceActive = action.value;
        context.selected.resetEmitter(0);
        break;
      case "randomDrift-checkbox":
        if (action.value) {
          context.selected.userData.system.emitters[0].behaviours.push({
            type: "RandomDrift",
            properties: {
              x: context.selected.userData.randomDriftX,
              y: context.selected.userData.randomDriftY,
              z: context.selected.userData.randomDriftZ,
            },
          });
        } else {
          context.selected.userData.system.emitters[0].behaviours = context.selected.userData.system.emitters[0].behaviours.filter(
            behavior => {
              return behavior.type !== "RandomDrift";
            },
          );
        }
        context.selected.userData.isRandomDriftActive = action.value;
        context.selected.resetEmitter(0);
        break;
      case "spring-checkbox":
        if (action.value) {
          context.selected.userData.system.emitters[0].behaviours.push({
            type: "Spring",
            properties: {
              x: context.selected.userData.springX,
              y: context.selected.userData.springY,
              z: context.selected.userData.springZ,
              spring: context.selected.userData.springFactor,
              friction: context.selected.userData.springFriction,
            },
          });
        } else {
          context.selected.userData.system.emitters[0].behaviours = context.selected.userData.system.emitters[0].behaviours.filter(
            behavior => {
              return behavior.type !== "Spring";
            },
          );
        }
        context.selected.userData.isSpringActive = action.value;
        context.selected.resetEmitter(0);
        break;
      case "preset":
        context.selected.setPreset(action.value);
        context.selected.updateParametersFromUserData(0);
        context.selected.resetEmitter(0);
        break;
      default:
        break;
    }
  } else if (category === "spriteCenter") {
    switch (action.name) {
      case "centerX":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.center.x = action.value;
        context.selected.userData.guiTransform.center.x = action.value;
        break;
      case "centerY":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.center.y = action.value;
        context.selected.userData.guiTransform.center.y = action.value;
        break;
      default:
        break;
    }
    context.selected.traverse(sprite => context.updateSpriteTransform(sprite));
  } else if (category === "spriteAnchor") {
    switch (action.name) {
      case "anchorX":
        context.selected.userData.guiTransform.anchor.x = action.value;
        break;
      case "anchorY":
        context.selected.userData.guiTransform.anchor.y = action.value;
        break;
      default:
        break;
    }
    context.updateSpriteTransform(context.selected);
  } else if (category === "spriteOffset") {
    let originalValue;
    let conversionFactor;
    switch (action.name) {
      case "offsetX":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.guiTransform.offset.x.value = action.value;
        break;
      case "offsetUnitX":
        if (context.selected.userData.guiTransform.offset.x.unit === action.value) break;
        originalValue = context.selected.userData.guiTransform.offset.x.value;
        const parentWidth =
          context.selected.parent.type === "Sprite" ? context.selected.parent.getWorldScale(_vector3).x : context.canvas.clientWidth;
        conversionFactor = action.value === "px" ? parentWidth / 100 : 100 / parentWidth;
        context.selected.userData.guiTransform.offset.x.value = Number((originalValue * conversionFactor).toFixed(2));
        context.selected.userData.guiTransform.offset.x.unit = action.value;
        break;
      case "offsetY":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.guiTransform.offset.y.value = action.value;
        break;
      case "offsetUnitY":
        if (context.selected.userData.guiTransform.offset.y.unit === action.value) break;
        originalValue = context.selected.userData.guiTransform.offset.y.value;
        const parentHeight =
          context.selected.parent.type === "Sprite" ? context.selected.parent.getWorldScale(_vector3).y : context.canvas.clientHeight;
        conversionFactor = action.value === "px" ? parentHeight / 100 : 100 / parentHeight;
        context.selected.userData.guiTransform.offset.y.value = Number((originalValue * conversionFactor).toFixed(2));
        context.selected.userData.guiTransform.offset.y.unit = action.value;
        break;
      default:
        break;
    }
    context.updateSpriteTransform(context.selected);
  } else if (category === "spriteSize") {
    let originalValue;
    let conversionFactor;
    switch (action.name) {
      case "sizeX":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.guiTransform.size.x.value = action.value;
        break;
      case "sizeUnitX":
        if (context.selected.userData.guiTransform.size.x.unit === action.value) break;
        originalValue = context.selected.userData.guiTransform.size.x.value;
        const parentWidth =
          context.selected.parent.type === "Sprite" ? context.selected.parent.getWorldScale(_vector3).x : context.canvas.clientWidth;
        conversionFactor = action.value === "px" ? parentWidth / 100 : 100 / parentWidth;
        context.selected.userData.guiTransform.size.x.value = Number((originalValue * conversionFactor).toFixed(2));
        context.selected.userData.guiTransform.size.x.unit = action.value;
        break;
      case "sizeY":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.guiTransform.size.y.value = action.value;
        break;
      case "sizeUnitY":
        if (context.selected.userData.guiTransform.size.y.unit === action.value) break;
        originalValue = context.selected.userData.guiTransform.size.y.value;
        const parentHeight =
          context.selected.parent.type === "Sprite" ? context.selected.parent.getWorldScale(_vector3).y : context.canvas.clientHeight;
        conversionFactor = action.value === "px" ? parentHeight / 100 : 100 / parentHeight;
        context.selected.userData.guiTransform.size.y.value = Number((originalValue * conversionFactor).toFixed(2));
        context.selected.userData.guiTransform.size.y.unit = action.value;
        break;
      case "aspectRatio":
        context.selected.userData.guiTransform.constantAspectRatio = action.value;
        break;
      default:
        break;
    }
    context.selected.traverse(sprite => context.updateSpriteTransform(sprite));
  } else if (category === "camera") {
    switch (action.name) {
      case "enabled":
        context.selected.userData.enabled = action.value;
        break;
      case "depth":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.userData.depth = action.value;
        break;
      case "fov":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.fov = action.value;
        context.selected.updateProjectionMatrix();
        break;
      case "left":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.left = action.value;
        context.selected.updateProjectionMatrix();
        break;
      case "right":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.right = action.value;
        context.selected.updateProjectionMatrix();
        break;
      case "top":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.top = action.value;
        context.selected.updateProjectionMatrix();
        break;
      case "bottom":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.bottom = action.value;
        context.selected.updateProjectionMatrix();
        break;
      case "near":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.near = action.value;
        context.selected.updateProjectionMatrix();
        break;
      case "far":
        if (!Number.isFinite(Number(action.value)) || action.value === "") break;
        context.selected.far = action.value;
        context.selected.updateProjectionMatrix();
        break;
      default:
        break;
    }
  }

  context.box.setFromObject(context.selected);
  if (context.box.isEmpty() === false) {
    context.selectionBox.setFromObject(context.selected);
    context.selectionBox.visible = true;
  }

  const helper = context.helpers[context.selected.id];
  if (helper && helper.update) helper.update();
  context.updateAudioHelper(context.selected);

  context.lastCmdTime = new Date();
  context.dispatchEvent(
    new CustomEvent(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, {
      detail: {object: context.selected},
    }),
  );

  context.render();
}
